import { useState, useCallback, useMemo, memo, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Check,
  ChevronRight,
  Save,
  Edit,
  Plus,
  Sun,
  Moon,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import { cn } from "@/lib/utils";
import { SaveThemeDialog } from "./save-theme-dialog";
import { CreateThemeDialog } from "./create-theme-dialog";
import { generateRandomPreviewColors, colorFormatter } from "../utils";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { themesApi } from "@/lib/api/themes";
import { commitsApi } from "@/lib/api/commits";
import { previewMessage } from "@/lib/visual-editor/post-message";
import { useResponsive } from "@/hooks/useResponsive";
import { useSystemTheme } from "@/hooks/use-system-theme";
import type { ThemeContent, Theme } from "@/types/theme.types";

interface ThemePreviewCardProps {
  theme: ThemeContent;
  workspaceId: string;
  previewIframe: HTMLIFrameElement | null | undefined;
  projectId: string;
  onEditTheme?: (theme: ThemeContent) => void;
  onThemeSaved?: (themeId: string) => void;
}

// 2x2 Color Grid Component
const ColorGrid = memo(
  ({
    colors,
    size = "md",
  }: {
    colors: string[];
    size?: "sm" | "md" | "lg";
  }) => {
    const sizeClasses = {
      sm: "w-8 h-8",
      md: "w-12 h-12",
      lg: "w-16 h-16",
    };
    const cellSizes = {
      sm: "w-4 h-4",
      md: "w-6 h-6",
      lg: "w-8 h-8",
    };

    return (
      <div
        className={cn(
          "grid grid-cols-2 gap-0.5 rounded-lg overflow-hidden",
          sizeClasses[size],
        )}
      >
        {colors.slice(0, 4).map((color, idx) => (
          <div
            key={idx}
            className={cn("", cellSizes[size])}
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
    );
  },
);

ColorGrid.displayName = "ColorGrid";

const ThemePreviewCardComponent = ({
  theme,
  workspaceId,
  previewIframe,
  projectId,
  onEditTheme,
  onThemeSaved,
}: ThemePreviewCardProps) => {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [themeName, setThemeName] = useState(theme.name);
  const [newThemeName, setNewThemeName] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const systemTheme = useSystemTheme();
  const [previewMode, setPreviewMode] = useState<"light" | "dark">(systemTheme);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [libraryThemes, setLibraryThemes] = useState<Theme[]>([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  // Selection state - only one can be selected at a time
  const [selectedSource, setSelectedSource] = useState<
    "generated" | "library" | null
  >("generated");
  const [selectedLibraryThemeId, setSelectedLibraryThemeId] = useState<
    string | null
  >(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const hasLoadedLibrary = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Mouse drag-to-scroll state for library themes
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, scrollLeft: 0 });
  const hasDraggedRef = useRef(false);
  const { getIdToken } = useAuth();
  const { isMobile } = useResponsive();

  // Send theme preview to iframe (reusable helper)
  // Use modeOverride when calling from within setState (previewMode hasn't committed yet)
  const sendThemeToIframe = useCallback(
    (iframe: HTMLIFrameElement, modeOverride?: "light" | "dark") => {
      const themeContent: ThemeContent = {
        name: theme.name,
        type: "registry:theme",
        cssVars: {
          light: theme.cssVars.light || {},
          dark: theme.cssVars.dark || {},
        },
      };
      previewMessage.updateTheme(
        iframe,
        theme.name,
        JSON.stringify(themeContent),
      );
      previewMessage.setThemeMode(iframe, modeOverride ?? previewMode);
    },
    [theme, previewMode],
  );

  // Sync with OS dark/light mode changes
  useEffect(() => {
    setPreviewMode(systemTheme);
    if (previewIframe) {
      sendThemeToIframe(previewIframe, systemTheme);
    }
  }, [systemTheme]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply generated theme preview on mount AND when visual editor becomes ready
  // The visual editor loads async via CDN — messages sent before it's ready are dropped.
  // Listen for "visual-editor-ready" to re-send theme after iframe is ready.
  useEffect(() => {
    if (!previewIframe) return;

    // Try sending immediately (works if visual editor already loaded)
    sendThemeToIframe(previewIframe);

    // Also listen for visual-editor-ready in case it hasn't loaded yet
    const handleReady = (event: MessageEvent) => {
      if (event.data?.type === "visual-editor-ready" && previewIframe) {
        sendThemeToIframe(previewIframe);
      }
    };
    window.addEventListener("message", handleReady);
    return () => window.removeEventListener("message", handleReady);
  }, [previewIframe, sendThemeToIframe]);

  // Fetch theme library on mount
  useEffect(() => {
    if (hasLoadedLibrary.current) return;
    hasLoadedLibrary.current = true;

    const fetchLibraryThemes = async () => {
      setIsLoadingLibrary(true);
      try {
        const token = await getIdToken();
        if (!token) return;

        const themes = await themesApi.listThemes(token, workspaceId);
        setLibraryThemes(themes);
      } catch (err) {
        console.error("Failed to fetch theme library:", err);
      } finally {
        setIsLoadingLibrary(false);
      }
    };

    fetchLibraryThemes();
  }, [getIdToken, workspaceId]);

  // Sync theme name and colors when theme prop changes (for different themes)
  useEffect(() => {
    setThemeName(theme.name);

    // Extract preview colors from theme
    if (theme.colors) {
      setPreviewColors(theme.colors);
    } else {
      const colorKeys = ["primary", "secondary", "accent", "muted"];
      const extractedColors = colorKeys.map((key) =>
        cssVarToHex(theme.cssVars.light[key] || "213 94% 68%"),
      );
      setPreviewColors(extractedColors);
    }
  }, [theme]);

  // Use light mode colors for display
  const vars = theme.cssVars.light;

  /**
   * Convert CSS variable to hex color
   * Handles both formats:
   * - OKLCH: "oklch(0.98 0.01 286.50)" (from Claude AI theme generation)
   * - HSL: "213 94% 68%" (from shadcn/ui themes)
   */
  const cssVarToHex = (cssVarValue: string): string => {
    try {
      // Handle empty or invalid values
      if (!cssVarValue || typeof cssVarValue !== "string") {
        return "#000000";
      }

      // Trim whitespace
      const trimmedValue = cssVarValue.trim();

      // Check if it's already in OKLCH format with wrapper
      if (trimmedValue.startsWith("oklch(")) {
        // Convert OKLCH to hex using colorFormatter
        return colorFormatter(trimmedValue, "hex");
      }

      // Check if it's in HSL format with wrapper
      if (trimmedValue.startsWith("hsl(")) {
        return colorFormatter(trimmedValue, "hex");
      }

      // Otherwise, assume it's HSL without wrapper (e.g., "213 94% 68%")
      // Wrap in hsl() for color converter
      const hslColor = `hsl(${trimmedValue})`;
      return colorFormatter(hslColor, "hex");
    } catch (error) {
      console.warn("Failed to convert CSS var to hex:", cssVarValue, error);
      return "#000000";
    }
  };

  /**
   * Extract 4 preview colors from Claude's generated theme
   * Priority: primary, secondary, accent, muted
   */
  const extractPreviewColorsFromTheme = (): string[] => {
    const colorKeys = ["primary", "secondary", "accent", "muted"];
    return colorKeys.map((key) => cssVarToHex(vars[key] || "213 94% 68%"));
  };

  // Initialize preview colors from theme (not random)
  const [previewColors, setPreviewColors] = useState<string[]>(
    () => theme.colors || extractPreviewColorsFromTheme(),
  );

  // Handle selecting the generated theme
  const handleSelectGenerated = useCallback(() => {
    if (!previewIframe) {
      console.warn("Preview iframe not available");
      return;
    }

    setSelectedSource("generated");
    setSelectedLibraryThemeId(null);

    // Preview the generated theme
    const themeContent: ThemeContent = {
      name: theme.name,
      type: "registry:theme",
      cssVars: {
        light: theme.cssVars.light || {},
        dark: theme.cssVars.dark || {},
      },
    };

    previewMessage.updateTheme(
      previewIframe,
      theme.name,
      JSON.stringify(themeContent),
    );
  }, [previewIframe, theme]);

  // Handle mode toggle (light/dark)
  const handleModeToggle = useCallback(
    (mode: "light" | "dark") => {
      setPreviewMode(mode);
      if (!previewIframe) return;

      // Re-send theme content + set mode to ensure CSS vars are fresh
      if (selectedSource === "library" && selectedLibraryThemeId) {
        const libraryTheme = libraryThemes.find(
          (t) => t.themeId === selectedLibraryThemeId,
        );
        if (libraryTheme) {
          previewMessage.updateTheme(
            previewIframe,
            libraryTheme.themeId,
            libraryTheme.content,
          );
        }
      } else {
        const themeContent: ThemeContent = {
          name: theme.name,
          type: "registry:theme",
          cssVars: {
            light: theme.cssVars.light || {},
            dark: theme.cssVars.dark || {},
          },
        };
        previewMessage.updateTheme(
          previewIframe,
          theme.name,
          JSON.stringify(themeContent),
        );
      }

      previewMessage.setThemeMode(previewIframe, mode);
    },
    [previewIframe, selectedSource, selectedLibraryThemeId, libraryThemes, theme],
  );

  // Handle Apply & Save - opens the save dialog
  const handleApplyAndSave = useCallback(() => {
    setSaveDialogOpen(true);
  }, []);

  // Handle color change in dialog
  const handleColorChange = useCallback((index: number, color: string) => {
    setPreviewColors((prev) => {
      const newColors = [...prev];
      newColors[index] = color;
      return newColors;
    });
  }, []);

  // Handle shuffle colors - regenerate random colors
  const handleShuffleColors = useCallback(() => {
    setPreviewColors(generateRandomPreviewColors());
  }, []);

  // Handle save confirmation

  const applyThemeToProject = useCallback(
    async (themeId: string, themeName: string) => {
      const token = await getIdToken();
      if (!token) return;

      setIsApplying(true);
      try {
        const applyResult = await themesApi.applyTheme(
          token,
          projectId,
          themeId,
        );

        // Auto-create commit after successful theme application
        try {
          const API_URL = import.meta.env.VITE_API_URL || "";

          // Generate AI commit message
          let commitMessage = `Applied theme: ${themeName}`;
          try {
            const generateResponse = await axios.post(
              `${API_URL}/api/projects/${projectId}/commits/generate-message`,
              {
                changedFiles: ["src/index.css"],
                additionalContext: `Theme applied: "${themeName}" (themeId: ${themeId}). This updates the CSS variables for colors, fonts, spacing, and other design tokens.`,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );
            commitMessage = generateResponse.data.message;
          } catch (aiError) {
            console.warn(
              "Failed to generate AI commit message, using fallback:",
              aiError,
            );
          }

          // Create commit with generated message
          await commitsApi.createCommit(projectId, commitMessage);
        } catch (commitError) {
          // Don't fail the theme apply if commit fails, just log it
          console.warn(
            "Failed to auto-create commit after theme apply:",
            commitError,
          );
        }

        // Bump slide thumbnail version if server captured new thumbnails
        if (applyResult?.slideThumbnailsUpdated) {
          const { useSlideStore } = await import("@/stores/slide-store");
          useSlideStore.getState().bumpThumbnailVersion();
        }

        // Show success toast after everything is done
        toast.success(`Theme "${themeName}" has been updated`);
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to apply theme";
        toast.error(errorMessage);
      } finally {
        setIsApplying(false);
      }
    },
    [getIdToken, projectId],
  );

  // Normalize CSS variables - remove -- prefix if present (backend expects keys without --)
  const normalizeCssVars = (
    cssVars: Record<string, string>,
  ): Record<string, string> => {
    const normalized: Record<string, string> = {};
    Object.entries(cssVars).forEach(([key, value]) => {
      // Remove -- prefix if present
      const normalizedKey = key.startsWith("--") ? key.substring(2) : key;
      normalized[normalizedKey] = value;
    });
    return normalized;
  };

  // Save as custom theme and apply it (from AI-generated theme)
  const handleSaveAsCustom = useCallback(async () => {
    if (!themeName.trim()) {
      toast.error("Please enter a theme name");
      return;
    }

    // Get auth token
    const token = await getIdToken();
    if (!token) {
      toast.error("Please sign in to save themes");
      return;
    }

    setIsSaving(true);
    try {
      // Normalize CSS variables (remove -- prefix if present)
      const normalizedCssVars = {
        light: normalizeCssVars(theme.cssVars.light || {}),
        dark: normalizeCssVars(theme.cssVars.dark || {}),
      };

      // Build theme content directly from the theme prop (AI-generated theme)
      const content = {
        name: themeName.trim(),
        type: "registry:theme",
        cssVars: normalizedCssVars,
      };

      // Create the theme
      const result = await themesApi.createTheme(token, workspaceId, {
        name: themeName.trim(),
        content: JSON.stringify(content),
        colors: previewColors,
      });

      setSaveDialogOpen(false);
      setThemeName("");

      // Notify parent to reload themes list
      if (result.themeId && onThemeSaved) {
        onThemeSaved(result.themeId);
      }

      // Apply the newly saved theme to the project
      if (result.themeId) {
        await applyThemeToProject(
          result.themeId,
          result.name || themeName.trim(),
        );

        // Update the preview iframe with the new theme
        if (previewIframe) {
          previewMessage.updateTheme(
            previewIframe,
            result.themeId,
            JSON.stringify(content),
          );
        }
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to save theme";
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [
    themeName,
    theme.cssVars,
    previewColors,
    getIdToken,
    workspaceId,
    applyThemeToProject,
    previewIframe,
    onThemeSaved,
  ]);

  // Handle edit theme - navigate to theme editor
  const handleEditTheme = useCallback(() => {
    if (onEditTheme) {
      onEditTheme(theme);
    }
  }, [onEditTheme, theme]);

  // Handle library theme click - preview instantly
  const handleLibraryThemeClick = useCallback(
    (libraryTheme: Theme) => {
      if (!previewIframe) {
        console.warn(
          "[ThemePreviewCard] previewIframe is null, cannot preview",
        );
        return;
      }

      // Clear generated selection, set library selection
      setSelectedSource("library");
      setSelectedLibraryThemeId(libraryTheme.themeId);

      try {
        const content = JSON.parse(libraryTheme.content) as ThemeContent;
        previewMessage.updateTheme(
          previewIframe,
          libraryTheme.themeId,
          JSON.stringify(content),
        );
      } catch (err) {
        console.error("Failed to preview library theme:", err);
      }
    },
    [previewIframe],
  );

  // Handle apply library theme - apply to project
  const handleApplyLibraryTheme = useCallback(
    async (libraryTheme: Theme) => {
      const token = await getIdToken();
      if (!token) {
        toast.error("Please sign in to apply themes");
        return;
      }

      await applyThemeToProject(libraryTheme.themeId, libraryTheme.name);
      if (onThemeSaved) {
        onThemeSaved(libraryTheme.themeId);
      }
    },
    [getIdToken, applyThemeToProject, onThemeSaved],
  );

  // Handle open create dialog - reset state and open dialog
  const handleOpenCreateDialog = useCallback(() => {
    setNewThemeName(theme.name);
    setPreviewColors(theme.colors || extractPreviewColorsFromTheme());
    setCreateDialogOpen(true);
  }, [theme]);

  // Handle create theme (save without applying to project)
  const handleCreateTheme = useCallback(async () => {
    if (!newThemeName.trim()) {
      toast.error("Please enter a theme name");
      return;
    }

    const token = await getIdToken();
    if (!token) {
      toast.error("Please sign in to save themes");
      return;
    }

    setIsCreating(true);
    try {
      // Normalize CSS variables (remove -- prefix if present)
      const normalizedCssVars = {
        light: normalizeCssVars(theme.cssVars.light || {}),
        dark: normalizeCssVars(theme.cssVars.dark || {}),
      };

      // Build theme content
      const content = {
        name: newThemeName.trim(),
        type: "registry:theme",
        cssVars: normalizedCssVars,
      };

      // Create the theme
      const result = await themesApi.createTheme(token, workspaceId, {
        name: newThemeName.trim(),
        content: JSON.stringify(content),
        colors: previewColors,
      });

      setCreateDialogOpen(false);
      setNewThemeName("");

      toast.success(`Theme "${newThemeName.trim()}" saved successfully`);

      // Notify parent to reload themes list
      if (result.themeId && onThemeSaved) {
        onThemeSaved(result.themeId);
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to save theme";
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  }, [
    newThemeName,
    theme.cssVars,
    previewColors,
    getIdToken,
    workspaceId,
    onThemeSaved,
  ]);

  // Mouse drag-to-scroll handlers for library themes horizontal scroll
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setIsDragging(true);
    hasDraggedRef.current = false;
    dragStartRef.current = { x: e.pageX, scrollLeft: container.scrollLeft };
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      const container = scrollContainerRef.current;
      if (!container) return;
      e.preventDefault();
      const dx = e.pageX - dragStartRef.current.x;
      if (Math.abs(dx) > 3) hasDraggedRef.current = true;
      container.scrollLeft = dragStartRef.current.scrollLeft - dx;
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Extract 4 colors for the 2x2 grid preview
  const featuredColors = useMemo(() => {
    const colorKeys = ["primary", "secondary", "accent", "muted"];
    return colorKeys.map((key) => cssVarToHex(vars[key] || "213 94% 68%"));
  }, [vars]);

  // Get the currently selected theme info for the bottom action bar
  const selectedThemeName =
    selectedSource === "generated"
      ? theme.name
      : libraryThemes.find((t) => t.themeId === selectedLibraryThemeId)?.name ||
        "";

  // Full theme preview card with modern, clean design
  return (
    <Card
      ref={cardRef}
      className="overflow-hidden border border-border/60 shadow-sm bg-card w-full max-w-full"
    >
      {/* Header with Light/Dark Toggle */}
      <div
        className={cn(
          "flex items-center justify-between",
          isMobile ? "px-3 pt-3 pb-2" : "px-4 pt-4 pb-2",
        )}
      >
        <span className="text-xs font-medium text-muted-foreground">
          Choose a theme
        </span>
        <div className="flex border border-border/40 rounded-md overflow-hidden flex-shrink-0">
          <button
            onClick={() => handleModeToggle("light")}
            className={cn(
              "p-1.5 transition-colors",
              previewMode === "light"
                ? "bg-primary text-primary-foreground"
                : "bg-muted/50 text-muted-foreground hover:bg-muted",
            )}
            title="Light mode"
          >
            <Sun className="w-3 h-3" />
          </button>
          <button
            onClick={() => handleModeToggle("dark")}
            className={cn(
              "p-1.5 transition-colors",
              previewMode === "dark"
                ? "bg-primary text-primary-foreground"
                : "bg-muted/50 text-muted-foreground hover:bg-muted",
            )}
            title="Dark mode"
          >
            <Moon className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Generated Theme - Clickable Card */}
      <div className={cn(isMobile ? "px-3 pb-3" : "px-4 pb-3")}>
        <button
          onClick={handleSelectGenerated}
          className={cn(
            "w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left",
            "hover:bg-accent/50",
            selectedSource === "generated"
              ? "bg-accent ring-2 ring-primary"
              : "bg-muted/30",
          )}
        >
          {/* 2x2 Color Grid */}
          <div className="relative flex-shrink-0">
            <ColorGrid colors={featuredColors} size={isMobile ? "sm" : "md"} />
            {selectedSource === "generated" && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                <Check className="w-2.5 h-2.5 text-primary-foreground" />
              </div>
            )}
          </div>

          {/* Theme Info */}
          <div className="flex-1 min-w-0">
            <h3
              className={cn(
                "font-medium text-foreground truncate",
                isMobile ? "text-sm" : "text-xs",
              )}
            >
              {theme.name}
            </h3>
            <p className="text-[10px] text-muted-foreground">AI Generated</p>
          </div>

          {/* Quick Actions - hide on mobile to save space */}
          {!isMobile && (
            <div
              className="flex items-center gap-1 flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              {onEditTheme && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEditTheme}
                  className="h-7 w-7 p-0"
                  title="Edit theme"
                >
                  <Edit className="w-3 h-3" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleOpenCreateDialog}
                disabled={isCreating}
                className="h-7 w-7 p-0"
                title="Save to library"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          )}
        </button>
      </div>

      {/* Theme Library - Horizontal Scroll */}
      <div className="border-t border-border/40 bg-muted/30">
        <div className={cn(isMobile ? "px-3 py-2" : "px-4 py-3")}>
          <div
            className={cn(
              "flex items-center justify-between",
              isMobile ? "mb-2" : "mb-3",
            )}
          >
            <span className="text-xs font-medium text-muted-foreground">
              Or pick from library
            </span>
            {libraryThemes.length > 0 && (
              <span className="text-[10px] text-muted-foreground/60">
                {libraryThemes.length} themes
              </span>
            )}
          </div>

          {isLoadingLibrary ? (
            <div className="flex items-center gap-2 py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                Loading themes...
              </span>
            </div>
          ) : libraryThemes.length === 0 ? (
            <div className="text-center py-4 text-xs text-muted-foreground">
              No themes available
            </div>
          ) : (
            /* Grid wrapper forces width calculation from parent, not children */
            <div className="grid grid-cols-1">
              <div
                ref={scrollContainerRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                className={cn(
                  "flex overflow-x-auto pb-2 pt-1 px-0.5 scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent select-none",
                  isMobile ? "gap-2" : "gap-3",
                  isDragging ? "cursor-grabbing" : "cursor-grab",
                )}
              >
                {libraryThemes.map((libraryTheme) => {
                  const isSelected =
                    selectedSource === "library" &&
                    selectedLibraryThemeId === libraryTheme.themeId;
                  return (
                    <button
                      key={libraryTheme.id}
                      onClick={() => {
                        if (hasDraggedRef.current) return;
                        handleLibraryThemeClick(libraryTheme);
                      }}
                      className={cn(
                        "flex flex-col items-center gap-1 p-2 rounded-lg transition-all flex-shrink-0",
                        "hover:bg-accent/80",
                        isMobile ? "w-[60px]" : "w-[72px]",
                        isSelected
                          ? "bg-accent ring-2 ring-primary"
                          : "bg-background",
                      )}
                      title={`${libraryTheme.name} - Click to select`}
                    >
                      <div className="relative">
                        <ColorGrid colors={libraryTheme.colors} size="sm" />
                        {isSelected && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-primary-foreground" />
                          </div>
                        )}
                      </div>
                      <span
                        className={cn(
                          "text-[10px] font-medium truncate w-full text-center",
                          isSelected
                            ? "text-foreground"
                            : "text-muted-foreground",
                        )}
                      >
                        {libraryTheme.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Unified Action Bar - Shows selected theme and apply button */}
      <div className="border-t border-border/40 bg-muted/50">
        <div
          className={cn(
            isMobile
              ? "px-3 py-2 flex flex-col gap-2"
              : "px-4 py-3 flex items-center justify-between",
          )}
        >
          <span
            className={cn(
              "font-medium text-foreground truncate",
              isMobile ? "text-xs" : "text-sm max-w-[200px]",
            )}
          >
            {selectedThemeName}
          </span>
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              if (selectedSource === "generated") {
                handleApplyAndSave();
              } else if (
                selectedSource === "library" &&
                selectedLibraryThemeId
              ) {
                const selected = libraryThemes.find(
                  (t) => t.themeId === selectedLibraryThemeId,
                );
                if (selected) handleApplyLibraryTheme(selected);
              }
            }}
            disabled={isApplying || isSaving || !selectedSource}
            className={cn(
              "text-xs gap-1.5",
              isMobile ? "h-9 w-full" : "h-8 px-4",
            )}
          >
            {isApplying || isSaving ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
            {selectedSource === "generated" ? "Save & Apply" : "Apply Theme"}
          </Button>
        </div>
      </div>

      {/* Save Theme Dialog (Apply & Save) */}
      <SaveThemeDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        themeName={themeName}
        onThemeNameChange={setThemeName}
        previewColors={previewColors}
        onColorChange={handleColorChange}
        onShuffleColors={handleShuffleColors}
        onSave={handleSaveAsCustom}
        isSaving={isSaving}
        isApplying={false}
      />

      {/* Create Theme Dialog (Save as New) */}
      <CreateThemeDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        themeName={newThemeName}
        onThemeNameChange={setNewThemeName}
        previewColors={previewColors}
        onColorChange={handleColorChange}
        onShuffleColors={handleShuffleColors}
        onCreate={handleCreateTheme}
        isCreating={isCreating}
      />
    </Card>
  );
};

// Memoize component to prevent unnecessary re-renders
export const ThemePreviewCard = memo(ThemePreviewCardComponent);
